import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { ApiError, type components } from '@akillidernek/api-client'
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Dialog,
  EmptyState,
  Field,
  Icon,
  Input,
  useToast,
} from '@akillidernek/ui'
import { api } from '../api'
import { useTenantSlug } from '../use-tenant'
import { AyarlarSekmeleri } from './ayarlar-sekmeler'

type Domain = components['schemas']['DomainRead']
type Status = Domain['status']

/**
 * Durum → görünüm. Dört durum, dört anlam; kullanıcı "pending" değil
 * "TXT bekleniyor" okumalı ve ne yapması gerektiğini görmeli.
 */
const DURUM: Record<
  Status,
  { label: string; tone: 'info' | 'success' | 'warning'; aciklama: string }
> = {
  pending: {
    label: 'DNS kaydı bekleniyor',
    tone: 'info',
    aciklama: 'Aşağıdaki TXT ve CNAME kayıtlarını alan adınızın DNS panelinde oluşturun.',
  },
  verified: {
    label: 'Doğrulandı, yönlendirme bekleniyor',
    tone: 'info',
    aciklama: 'TXT kaydı bulundu. CNAME kaydı yayılınca alan adı otomatik olarak yayına girer.',
  },
  live: { label: 'Yayında', tone: 'success', aciklama: 'Sertifika alındı; alan adı çalışıyor.' },
  failed: {
    label: 'Doğrulama başarısız',
    tone: 'warning',
    aciklama:
      'Kayıtlar art arda bulunamadı. DNS panelinizi kontrol edip "Şimdi kontrol et" ile yeniden deneyin.',
  },
}

/** Ayarlar › Alan adları — YES-165 (YES-113 epiği). */
export function AyarlarAlanAdlari() {
  const slug = useTenantSlug()
  const [ekleAcik, setEkleAcik] = useState(false)
  const [secili, setSecili] = useState<string | null>(null)

  const alanAdlari = useQuery({
    queryKey: ['domains', slug],
    enabled: Boolean(slug),
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/domains')
      return data ?? []
    },
    // Worker saatte bir kontrol ediyor; ekran açıkken dakikada bir yenilemek,
    // "Şimdi kontrol et"e basmadan da değişimi gösterir.
    refetchInterval: 60_000,
  })

  if (alanAdlari.isError) {
    const yetkisiz = alanAdlari.error instanceof ApiError && alanAdlari.error.problem.status === 403
    return (
      <Sayfa>
        <Card>
          <CardBody className="text-sm">
            {yetkisiz ? 'Alan adlarını görme yetkiniz yok.' : 'Alan adları yüklenemedi.'}
          </CardBody>
        </Card>
      </Sayfa>
    )
  }

  const seciliAlan = alanAdlari.data?.find((alan) => alan.hostname === secili) ?? null

  return (
    <Sayfa>
      <Card>
        <CardHeader
          title="Alan adları"
          description="Derneğinizin web sitesi kendi alan adınızda yayınlanır. Sertifika otomatik alınır."
          action={<Button onClick={() => setEkleAcik(true)}>Alan adı ekle</Button>}
        />
        <CardBody>
          {alanAdlari.data && alanAdlari.data.length > 0 ? (
            <ul className="flex flex-col gap-2">
              {alanAdlari.data.map((alan) => (
                <li key={alan.hostname}>
                  <button
                    type="button"
                    onClick={() => setSecili(alan.hostname)}
                    className="flex w-full flex-wrap items-center gap-3 rounded-[var(--radius-control)] border border-[var(--ad-border)] px-3 py-2.5 text-left hover:bg-[var(--ad-surface-muted)]"
                  >
                    <span className="font-mono text-sm">{alan.hostname}</span>
                    {alan.is_primary && <Badge tone="neutral">Birincil</Badge>}
                    <Badge tone={DURUM[alan.status].tone} className="ml-auto">
                      {DURUM[alan.status].label}
                    </Badge>
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <EmptyState
              icon={<Icon name="globe" size={24} aria-hidden />}
              title="Henüz alan adı yok"
              description="Siteniz şimdilik akillidernek.com altındaki adresinizde yayında. Kendi alan adınızı bağlamak için ekleyin."
            />
          )}
        </CardBody>
      </Card>

      {ekleAcik && (
        <AlanAdiEkle slug={slug} onKapat={() => setEkleAcik(false)} onEklendi={setSecili} />
      )}
      {seciliAlan && <AlanAdiDetay alan={seciliAlan} slug={slug} onKapat={() => setSecili(null)} />}
    </Sayfa>
  )
}

function AlanAdiEkle({
  slug,
  onKapat,
  onEklendi,
}: {
  slug: string | null
  onKapat: () => void
  onEklendi: (hostname: string) => void
}) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [hatalar, setHatalar] = useState<Record<string, string>>({})
  const [hata, setHata] = useState<string | null>(null)

  const ekle = useMutation({
    mutationFn: async (form: FormData) => {
      const { data } = await api.POST('/api/v1/domains', {
        body: {
          hostname: String(form.get('hostname') ?? '')
            .trim()
            .toLowerCase()
            .replace(/^https?:\/\//, '')
            .replace(/\/.*$/, ''),
          is_primary: form.get('is_primary') !== null,
        },
      })
      return data!
    },
    onSuccess: (alan) => {
      void queryClient.invalidateQueries({ queryKey: ['domains', slug] })
      toast({
        title: 'Alan adı eklendi',
        description: 'Şimdi DNS kayıtlarını oluşturun.',
        tone: 'success',
      })
      onKapat()
      // Eklenir eklenmez ayrıntı açılıyor: kullanıcının bir sonraki adımı DNS
      // kayıtlarını kopyalamak, listeye dönüp tekrar tıklamak değil.
      onEklendi(alan.hostname)
    },
    onError: (error) => {
      if (error instanceof ApiError) {
        setHatalar(error.fieldErrors())
        setHata(error.problem.detail)
      }
    },
  })

  return (
    <Dialog
      open
      onOpenChange={(acik) => !acik && onKapat()}
      title="Alan adı ekle"
      description="Alan adının size ait olduğunu bir TXT kaydıyla doğrularız; sertifikayı biz alırız."
    >
      <form
        className="flex flex-col gap-3"
        onSubmit={(event) => {
          event.preventDefault()
          setHata(null)
          ekle.mutate(new FormData(event.currentTarget))
        }}
      >
        <Field
          label="Alan adı"
          required
          error={hatalar.hostname}
          hint="www ile veya www'suz; ikisini de biz yönetiriz."
        >
          {(props) => (
            <Input name="hostname" placeholder="www.dernegim.org.tr" autoFocus {...props} />
          )}
        </Field>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" name="is_primary" />
          Birincil adres olsun (diğer adresler buraya yönlenir)
        </label>
        {hata && (
          <p role="alert" className="text-xs text-[var(--ad-danger)]">
            {hata}
          </p>
        )}
        <div className="mt-1 flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onKapat}>
            Vazgeç
          </Button>
          <Button type="submit" disabled={ekle.isPending}>
            {ekle.isPending ? 'Ekleniyor…' : 'Ekle'}
          </Button>
        </div>
      </form>
    </Dialog>
  )
}

function Kayit({ etiket, ad, deger }: { etiket: string; ad: string; deger: string }) {
  const toast = useToast()
  return (
    <div className="grid gap-1 rounded-[var(--radius-control)] bg-[var(--ad-surface-muted)] p-3 text-sm sm:grid-cols-[4rem_1fr_auto]">
      <span className="font-medium">{etiket}</span>
      <dl className="grid grid-cols-[3.5rem_1fr] gap-x-2 gap-y-0.5 font-mono text-xs">
        <dt className="text-[var(--ad-text-muted)]">Ad</dt>
        <dd className="select-all break-all">{ad}</dd>
        <dt className="text-[var(--ad-text-muted)]">Değer</dt>
        <dd className="select-all break-all">{deger}</dd>
      </dl>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => {
          void navigator.clipboard?.writeText(deger)
          toast({ title: 'Kopyalandı', tone: 'success' })
        }}
      >
        Değeri kopyala
      </Button>
    </div>
  )
}

function AlanAdiDetay({
  alan,
  slug,
  onKapat,
}: {
  alan: Domain
  slug: string | null
  onKapat: () => void
}) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const tazele = () => void queryClient.invalidateQueries({ queryKey: ['domains', slug] })

  const kontrol = useMutation({
    mutationFn: async () => {
      const { data } = await api.POST('/api/v1/domains/{hostname}/verify', {
        params: { path: { hostname: alan.hostname } },
      })
      return data!
    },
    onSuccess: (guncel) => {
      tazele()
      toast({
        title: guncel.status === 'live' ? 'Yayında' : DURUM[guncel.status].label,
        description: guncel.last_error ?? undefined,
        tone: guncel.status === 'failed' ? 'error' : 'success',
      })
    },
    onError: (error) =>
      toast({
        title: 'Kontrol edilemedi',
        description: error instanceof ApiError ? error.problem.detail : undefined,
        tone: 'error',
      }),
  })

  const birincil = useMutation({
    mutationFn: async () => {
      await api.POST('/api/v1/domains/{hostname}/primary', {
        params: { path: { hostname: alan.hostname } },
      })
    },
    onSuccess: () => {
      tazele()
      toast({ title: 'Birincil adres güncellendi', tone: 'success' })
    },
    onError: (error) =>
      toast({
        title: 'Yapılamadı',
        description: error instanceof ApiError ? error.problem.detail : undefined,
        tone: 'error',
      }),
  })

  const kaldir = useMutation({
    mutationFn: async () => {
      await api.DELETE('/api/v1/domains/{hostname}', {
        params: { path: { hostname: alan.hostname } },
      })
    },
    onSuccess: () => {
      tazele()
      onKapat()
      toast({ title: 'Alan adı kaldırıldı', tone: 'success' })
    },
    onError: (error) =>
      toast({
        title: 'Kaldırılamadı',
        description: error instanceof ApiError ? error.problem.detail : undefined,
        tone: 'error',
      }),
  })

  const durum = DURUM[alan.status]

  return (
    <Dialog
      open
      onOpenChange={(acik) => !acik && onKapat()}
      title={alan.hostname}
      description={durum.aciklama}
    >
      <div className="flex flex-col gap-4 text-sm">
        <div className="flex flex-wrap items-center gap-2">
          <Badge tone={durum.tone}>{durum.label}</Badge>
          {alan.is_primary && <Badge tone="neutral">Birincil</Badge>}
          {alan.last_checked_at && (
            <span className="text-xs text-[var(--ad-text-muted)]">
              Son kontrol {new Date(alan.last_checked_at).toLocaleString('tr-TR')}
            </span>
          )}
        </div>

        {/* Hata metni sunucudan geldiği gibi: "DNS'i kontrol edin" demek
            yerine hangi kaydın bulunamadığını söylüyor. */}
        {alan.last_error && alan.status !== 'live' && (
          <p className="rounded-[var(--radius-control)] bg-[var(--ad-warning)]/15 px-3 py-2 text-xs">
            {alan.last_error}
          </p>
        )}

        {alan.status !== 'live' && (
          <div className="flex flex-col gap-2">
            <p className="text-xs text-[var(--ad-text-muted)]">
              Alan adınızın DNS panelinde şu iki kaydı oluşturun. Yayılma birkaç dakikadan birkaç
              saate kadar sürebilir; biz saatte bir kontrol ediyoruz.
            </p>
            <Kayit etiket="TXT" ad={alan.txt_record_name} deger={alan.txt_record_value} />
            <Kayit etiket="CNAME" ad={alan.hostname} deger={alan.cname_target} />
          </div>
        )}

        <div className="flex flex-wrap items-center gap-2 border-t border-[var(--ad-border)] pt-3">
          <Button
            type="button"
            variant="ghost"
            disabled={kaldir.isPending}
            onClick={() => kaldir.mutate()}
          >
            Kaldır
          </Button>
          <div className="ml-auto flex gap-2">
            {!alan.is_primary && alan.status === 'live' && (
              <Button
                type="button"
                variant="secondary"
                disabled={birincil.isPending}
                onClick={() => birincil.mutate()}
              >
                Birincil yap
              </Button>
            )}
            {alan.status !== 'live' && (
              <Button type="button" disabled={kontrol.isPending} onClick={() => kontrol.mutate()}>
                {kontrol.isPending ? 'Kontrol ediliyor…' : 'Şimdi kontrol et'}
              </Button>
            )}
          </div>
        </div>
      </div>
    </Dialog>
  )
}

function Sayfa({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-4">
      <h1 className="text-lg font-semibold">Ayarlar</h1>
      <AyarlarSekmeleri />
      {children}
    </div>
  )
}
